import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { getTurnTrackingPartiesNames } from '~/components/Workflow/TicketsViewPage/utils';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Select } from '~/eds/src/components/Select/Select';
import { InputWidth } from '~/eds/src/types/InputWidth';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';
import {
  TurnTrackingHistoryType,
  TurnTrackingPartyType,
} from '~/types/TurnTracking';
import { Uuid } from '~/types/Uuid';

interface Props {
  history?: TurnTrackingHistoryType[];
  /* Either pass an array of parties or a workflow id for component to make api call */
  parties?: TurnTrackingPartyType[];
  workflowId?: Uuid;
  showDescription?: boolean;
  /* Error message for failed api call, provide if "parties" not passed */
  errorMessage?: { message: string; title?: string };
  width?: InputWidth;
  onChange: (partyId: Nullable<Uuid>) => void;
}

export const PartySelectField = ({
  history,
  parties = [],
  workflowId,
  showDescription = false,
  errorMessage,
  width = '100%',
  onChange,
}: Props) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { data: rawData = [], isError } =
    api.endpoints.getTurnTrackingParties.useQuery(
      {
        workflowId: workflowId as Uuid,
      },
      { skip: parties?.length > 0 || !workflowId },
    );

  const turnTrackingPartiesNames = getTurnTrackingPartiesNames(intl);
  const data = rawData.map((party) => ({
    ...party,
    name: turnTrackingPartiesNames[party.name] ?? party.name, // Fallback to original name if not found
  }));

  const partyList = data.length > 0 ? data : parties;
  const showError = isError ?? false;

  useEffect(() => {
    if (showError && errorMessage?.message) {
      const message = errorMessage.message;
      const title = errorMessage?.title ? errorMessage.title : '';
      toast({ status: 'info', message, title });
    }
  }, [showError]);

  const selectHistory = useSelector(slices.turnTracking.selectors.history);
  const turnTrackingHistory = history || selectHistory;
  const currentParty = turnTrackingHistory?.[0]?.partyId;
  const partyOptions = partyList.map((party: TurnTrackingPartyType) => {
    if (currentParty && currentParty === party.id) {
      return {
        label: intl.formatMessage(
          {
            id: 'PartySelectField.currentlyWithLabel',
            defaultMessage: '{partyName} (currently with)',
            description:
              'Label for party that currently has the item. Example: partyName = "ABC Corporation"',
          },
          { partyName: party.name },
        ),
        value: party.id,
      };
    }
    return { label: party.name, value: party.id };
  });

  const [selectedPartyId, setSelectedPartyId] = useState<Nullable<Uuid>>(
    currentParty || null,
  );

  return partyList.length > 0 ? (
    <>
      {/*
        Candu injects the "Cycle Tracking" banner by targeting aria-label="time-tracker-select-party".
        This decoy div provides a proper target outside the combobox for accessibility (WCAG 1.3.1).
        See: EKP-43263
      */}
      <div aria-label="time-tracker-select-party" />
      <FormField<string, false>
        name="party-selector"
        label={intl.formatMessage({
          id: 'PartySelectField.responsiblePartyLabel',
          defaultMessage: 'Set responsible party',
          description: 'Label for party selection field',
        })}
        description={
          showDescription
            ? intl.formatMessage({
                id: 'PartySelectField.responsiblePartyDescription',
                defaultMessage:
                  "Select the party that's responsible for current cycle",
                description: 'Description for party selection field',
              })
            : ''
        }
        input={Select}
        inputProps={{
          options: partyOptions,
          isMulti: false,
          enablePortal: true,
          width,
        }}
        value={selectedPartyId}
        onChange={(partyId) => {
          setSelectedPartyId(partyId);
          onChange(partyId);
        }}
      />
    </>
  ) : (
    <Layout h="height.m" w="input.l.width">
      <LoadingShimmer />
    </Layout>
  );
};
